<script>
/* global axios */
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: { PageHeader, NextButton },
  data() {
    return {
      channelName: '',
      step: 'form',
      qrcode: null,
      connectionStatus: 'disconnected',
      pollingInterval: null,
      inboxId: null,
      connecting: false,
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    accountId() {
      return window.location.pathname.split('/')[3];
    },
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    async createChannel() {
      if (!this.channelName.trim()) return;

      try {
        const inbox = await this.$store.dispatch('inboxes/createChannel', {
          name: this.channelName,
          channel: { type: 'whatsapp_qr' },
        });
        this.inboxId = inbox.id;
        this.step = 'qr';
        await this.connectInstance();
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.WHATSAPP_QR.API.ERROR_MESSAGE')
        );
      }
    },

    async connectInstance() {
      this.connecting = true;
      try {
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inboxId}/uazapi/connect`
        );
        if (data.qrcode) this.qrcode = data.qrcode;
        if (data.status) this.connectionStatus = data.status;
        this.startPolling();
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.ADD.WHATSAPP_QR.API.ERROR_MESSAGE'));
      } finally {
        this.connecting = false;
      }
    },

    startPolling() {
      this.pollingInterval = setInterval(this.pollStatus, 3000);
    },

    stopPolling() {
      if (this.pollingInterval) clearInterval(this.pollingInterval);
    },

    async pollStatus() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inboxId}/uazapi/status`
        );
        this.connectionStatus = data.status;
        if (data.qrcode) this.qrcode = data.qrcode;

        if (data.status === 'connected') {
          this.stopPolling();
          this.step = 'connected';
        }
      } catch (_) {
        // ignore polling errors
      }
    },

    goToAgents() {
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: this.inboxId },
      });
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.WHATSAPP_QR.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.WHATSAPP_QR.DESC')"
    />

    <div v-if="step === 'form'" class="flex flex-wrap flex-col mx-0">
      <div class="flex-shrink-0 flex-grow-0">
        <label>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_QR.CHANNEL_NAME.LABEL') }}
          <input
            v-model="channelName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WHATSAPP_QR.CHANNEL_NAME.PLACEHOLDER')
            "
          />
        </label>
      </div>
      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.WHATSAPP_QR.SUBMIT_BUTTON')"
          @click="createChannel"
        />
      </div>
    </div>

    <div
      v-else-if="step === 'qr'"
      class="flex flex-col items-center gap-6 mt-4"
    >
      <div v-if="connecting" class="text-slate-600 text-sm">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_QR.CONNECTING') }}
      </div>
      <div v-else-if="qrcode" class="flex flex-col items-center gap-3">
        <p class="text-slate-700 text-sm">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_QR.SCAN_INSTRUCTION') }}
        </p>
        <img
          :src="qrcode"
          alt="WhatsApp QR Code"
          class="w-56 h-56 border rounded-lg"
        />
        <p class="text-xs text-slate-500">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_QR.QR_EXPIRY_NOTE') }}
        </p>
      </div>
      <div v-else class="text-slate-500 text-sm">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_QR.WAITING_QR') }}
      </div>
    </div>

    <div
      v-else-if="step === 'connected'"
      class="flex flex-col items-center gap-4 mt-6"
    >
      <div class="text-teal-600 text-lg font-medium">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_QR.CONNECTED') }}
      </div>
      <NextButton
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.WHATSAPP_QR.NEXT_BUTTON')"
        @click="goToAgents"
      />
    </div>
  </div>
</template>
